import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Legacy: kept for backward compatibility while browsers still hold a JWT
// in localStorage from the pre-Phase-10.2 rollout. Once such a browser
// logs out (or the token expires) the value is wiped and the new
// cookie-based flow takes over. New logins never write to localStorage.
export const TOKEN_KEY = "auth_token";
const CSRF_COOKIE = "csrf_token";

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 240000,
  // Send cookies (access_token httpOnly + csrf_token) on every request.
  // Required for the Phase 10.2 cookie-based auth flow.
  withCredentials: true,
});

// Small helper — read a cookie value by name from `document.cookie`.
// `csrf_token` is intentionally NOT httpOnly precisely so the SPA can
// echo it back in `X-CSRF-Token`.
function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"]);

// Attach auth headers on every request.
api.interceptors.request.use((config) => {
  const legacyToken = localStorage.getItem(TOKEN_KEY);
  if (legacyToken) {
    // Legacy tokens keep working via the backend's Bearer fallback.
    config.headers.Authorization = `Bearer ${legacyToken}`;
  }

  // Add CSRF header on mutating methods when we have a csrf_token cookie.
  // The backend middleware skips CSRF entirely for Bearer-authenticated
  // requests, but sending the header when both are present is harmless.
  const method = (config.method || "get").toLowerCase();
  if (MUTATING_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) {
      config.headers["X-CSRF-Token"] = csrf;
    }
  }
  return config;
});

// Phase 10.3 Fix (Batch 3) — 403 CSRF auto-recovery.
//
// After a service-worker/bundle upgrade a browser may hold a stale
// `csrf_token` cookie whose value no longer matches the header the
// interceptor computes on send (or the cookie was missing when the request
// left). Instead of failing hard, we:
//
//   1. Detect 403 responses whose `detail` mentions "CSRF" (the backend
//      always uses that word — see `services/csrf.py`).
//   2. Call `GET /api/auth/csrf` on the same axios instance to rehydrate
//      both the cookie (Set-Cookie roundtrip if the server refreshes it)
//      and the in-memory value we'll echo back.
//   3. Retry the original request ONCE with the freshly-read cookie value.
//   4. If it still fails, propagate the error normally.
//
// The `_csrfRetry` flag on `config` guards against a retry loop if the
// server keeps returning 403.
async function tryRecoverCsrf(originalConfig) {
  originalConfig._csrfRetry = true;
  try {
    // Use bare axios (no interceptors) to avoid re-triggering this handler
    // if /csrf itself somehow 403s.
    const res = await axios.get(`${API_BASE}/auth/csrf`, {
      withCredentials: true,
      timeout: 15000,
    });
    const fresh = res?.data?.csrf_token;
    if (!fresh) {
      // No session server-side either → let the caller see the 403.
      return null;
    }
    // Echo the new token on the retry. Reading from document.cookie would
    // also work (the /csrf response Set-Cookies it), but using the body
    // value is race-free.
    originalConfig.headers = originalConfig.headers || {};
    originalConfig.headers["X-CSRF-Token"] = fresh;
    return api.request(originalConfig);
  } catch (err) {
    return null;
  }
}

api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;

    // 403 CSRF auto-recovery.
    if (
      status === 403 &&
      typeof detail === "string" &&
      detail.includes("CSRF") &&
      !error.config?._csrfRetry
    ) {
      const retried = await tryRecoverCsrf(error.config);
      if (retried) return retried;
      // Recovery failed → fall through to the standard error path.
    }

    // 401 → clear any leftover legacy token and force re-login.
    if (status === 401) {
      const here = window.location.pathname;
      if (here !== "/login") {
        localStorage.removeItem(TOKEN_KEY);
        window.location.assign("/login?expired=1");
      }
    }
    return Promise.reject(error);
  }
);

// FastAPI 422 returns array of objects; format safely.
export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || "Error desconocido";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" · ");
  if (d?.msg) return d.msg;
  return String(d);
}
